import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, HelpCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { wizardService } from '../../services/wizardService';
import { extractApiError } from '../../services/tenantRegistrationService';
import type { ChangePasswordConfig } from '../../types/wizard';
import logoCorgee from '../../assets/logos/logo-corgee.png';

const CHANGE_PASSWORD_CONFIG_KEY = 'C2_changePasswordConfig';
const INITIAL_CUSTOMIZE_KEY = 'C2_initialCustomize';

const PASSWORD_RULES = [
  'Password must contain at least six characters.',
  'Password length allows sixteen characters maximum.',
  'Password must contain at least one number (0 - 9).',
  'Password must contain at least one lowercase letter (a - z).',
  'Password must contain at least one uppercase letter (A - Z).',
  'Password can not contain blank spaces.',
];

function readConfig(searchParams: URLSearchParams): ChangePasswordConfig | null
{
  // Query string (legacy link format) has priority over the stored login response
  const fromQuery: ChangePasswordConfig = {
    token: searchParams.get('token') || '',
    userId: searchParams.get('userId') || '',
    tenant: searchParams.get('tenant') || '',
    email: searchParams.get('email') || '',
  };
  if (fromQuery.token && fromQuery.userId) return fromQuery;

  try
  {
    const raw = localStorage.getItem(CHANGE_PASSWORD_CONFIG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ChangePasswordConfig;
    return parsed?.token && parsed?.userId ? parsed : null;
  }
  catch
  {
    return null;
  }
}

/**
 * Forced password change on the first login of a new tenant administrator
 * (migrated from the legacy UpdatePassword page). After the change it continues
 * to the initial wizard when the site has not been configured yet.
 */
export const UpdatePasswordPage: React.FC = () =>
{
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { validatePassword } = useAuth();
  const config = useMemo(() => readConfig(searchParams), [searchParams]);

  const [password, setPassword] = useState('');
  const [rePassword, setRePassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [result, setResult] = useState<'success' | 'invalid-token' | null>(null);

  const goBack = useCallback(() =>
  {
    localStorage.removeItem(CHANGE_PASSWORD_CONFIG_KEY);
    navigate('/', { replace: true });
  }, [navigate]);

  const handleUpdate = useCallback(async () =>
  {
    if (!config)
    {
      setMessage('The password reset information is missing. Please log in again.');
      return;
    }

    setMessage('');
    const validation = validatePassword(true, {
      userName: config.email,
      password,
      password2: rePassword,
    });
    if (validation.ResultId === 2)
    {
      setMessage(validation.Message);
      return;
    }

    setLoading(true);
    try
    {
      const ok = await wizardService.resetPassword(
        config.userId,
        config.token,
        password,
        config.tenant,
      );
      if (!ok)
      {
        setResult('invalid-token');
        return;
      }

      localStorage.removeItem(CHANGE_PASSWORD_CONFIG_KEY);

      if (localStorage.getItem(INITIAL_CUSTOMIZE_KEY) === 'false')
      {
        navigate('/InitialWizard', { replace: true });
        return;
      }
      setResult('success');
    }
    catch (err)
    {
      setMessage(extractApiError(err, 'The password could not be updated.'));
    }
    finally
    {
      setLoading(false);
    }
  }, [config, password, rePassword, validatePassword, navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) =>
    {
      if (e.key === 'Enter') handleUpdate();
    },
    [handleUpdate],
  );

  const title =
    result === 'success'
      ? 'Successful update'
      : result === 'invalid-token'
        ? 'Invalid token'
        : 'Update Password';

  return (
    <div className="min-h-screen bg-[#ededed] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md relative pt-20 mt-8">
        <div className="absolute -top-20 left-1/2 transform -translate-x-1/2">
          <div className="w-36 h-36 bg-white rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
            <img src={logoCorgee} alt="Corgee Logo" className="w-28 h-28 object-contain" />
          </div>
        </div>

        <div className="p-8 pb-6 text-center">
          <div className="relative inline-flex items-center gap-2 mb-6">
            <h2 className="text-2xl text-[#62accb] font-normal">{title}</h2>
            {result === null && (
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onMouseEnter={() => setShowRules(true)}
                onMouseLeave={() => setShowRules(false)}
                onClick={() => setShowRules(v => !v)}
                aria-label="Password rules"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            )}
            {showRules && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-10 w-72 rounded-md bg-gray-800 text-white text-xs text-left p-3 shadow-lg">
                <ul className="space-y-1">
                  {PASSWORD_RULES.map(rule => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {result === null ? (
            <>
              {config?.email && (
                <p className="text-sm text-gray-500 mb-4">
                  Set a new password for <span className="font-medium">{config.email}</span>
                </p>
              )}
              <div className="space-y-4 text-left">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="password"
                    placeholder="New Password"
                    autoComplete="new-password"
                    value={password}
                    onChange={e =>
                    {
                      setPassword(e.target.value);
                      setMessage('');
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#62accb] focus:border-transparent"
                    autoFocus
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="password"
                    placeholder="New Password Again"
                    autoComplete="new-password"
                    value={rePassword}
                    onChange={e =>
                    {
                      setRePassword(e.target.value);
                      setMessage('');
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#62accb] focus:border-transparent"
                  />
                </div>
              </div>

              {message && <p className="mt-3 text-sm text-red-500">{message}</p>}

              <button
                type="button"
                onClick={handleUpdate}
                disabled={loading || !config}
                className="mt-6 w-full py-2.5 bg-[#62accb] hover:bg-[#4a9bd1] text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
              {!config && (
                <p className="mt-3 text-xs text-red-500">
                  The password reset information is missing. Please log in again.
                </p>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={goBack}
              className="w-full py-2.5 bg-[#62accb] hover:bg-[#4a9bd1] text-white rounded-md text-sm font-medium transition-colors"
            >
              Back to Corgee
            </button>
          )}

          <div className="mt-6 text-center text-xs text-gray-400">
            Corgee © CGBOT {new Date().getFullYear()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePasswordPage;
